import type { ReactNode } from "react";
import { Icon, type IconName } from "../icons/Icon";
import { Avatar } from "../primitives/Avatar";
import { Pill } from "../primitives/Pill";
import { HeadCheck, RowCheck } from "../primitives/RowCheck";
import { Skeleton } from "../primitives/Skeleton";
import { cx } from "../utils/format";
import type { Navigate } from "./AppShell";
import { SampleChip } from "./Onboarding";
import { TagDot } from "./Tags";
import type { TagColor } from "../types";

/* ---------- customers table ---------- */
export type CustomerLicenseState = "verified" | "partly" | "unverified";
export interface CustomerRowData {
  id: string; href: string; initials: string; name: string; email: string; company?: string;
  tickets: number; open: number; /** formatted, or null when they never wrote */ lastContact: string | null;
  checked?: boolean;
  /** M11: the sample ticket's made-up customer (Anna Berger): the "Sample" chip beside her name, as on her ticket */ sample?: boolean;
  /** the customer tags round (2026-10-24): the person's customer tags, for the Tags column */ tags?: { id: string; name: string; color?: TagColor }[];
}
/** Column ids double as sort ids in the URL; `hide` drops the column on narrower shells. */
export interface CustomerColumn { id: string; label: string; sort?: string; numeric?: boolean; /** ellipsizes before the table overflows */ text?: boolean; /** the growing column: takes what the others leave and cuts last (2026-10-17) */ grow?: boolean; hide?: "narrow" | "phone"; }
/** Five columns: who, how to reach them, where they work, how much we have talked, and when last. Products, licenses, sites and
 *  renewals live on the profile and in the segments and sorts; as columns they made the table too wide to read. */
export const CUSTOMER_COLUMNS: CustomerColumn[] = [
  { id: "customer", label: "Name", sort: "name", grow: true }, { id: "email", label: "Email", sort: "email", text: true, hide: "phone" }, { id: "company", label: "Company", sort: "company", text: true, hide: "narrow" },
  { id: "tickets", label: "Tickets", sort: "tickets", numeric: true }, { id: "contact", label: "Last seen", sort: "recent", numeric: true, hide: "phone" },
];
/** the customer tags round (2026-10-24): the Tags column, after Company, for a table that passes `tags` */
const TAGS_COLUMN: CustomerColumn = { id: "tags", label: "Tags", hide: "phone" };
const columnsOf = (tags: boolean): CustomerColumn[] => (tags ? [...CUSTOMER_COLUMNS.slice(0, 3), TAGS_COLUMN, ...CUSTOMER_COLUMNS.slice(3)] : CUSTOMER_COLUMNS);


function Cell({ row, col }: { row: CustomerRowData; col: CustomerColumn }) {
  switch (col.id) {
    // the ticket style round (2026-10-26): the address under the name wherever the Email column is hidden (the same rules as `hide: "phone"`)
    case "customer": return <span className="ob-dtable__person"><Avatar initials={row.initials} size="sm" /><span className="ob-dtable__who"><span className="ob-dtable__name" title={row.name}>{row.name}</span><span className="ob-dtable__email ob-dtable__email--no-col" title={row.email}>{row.email}</span></span>{row.sample && <SampleChip className="ob-dtable__chip" />}</span>;
    case "email": return <span className="ob-dtable__muted">{row.email}</span>;
    case "company": return <>{row.company ?? <span className="ob-dtable__muted">—</span>}</>;
    case "tickets": return <>{row.tickets}{row.open > 0 && <span className="ob-dtable__muted"> · {row.open} open</span>}</>;
    case "tags": return <span className="ob-tags ob-dtable__tags">{(row.tags ?? []).map((t) => <span key={t.id} className="ob-tag"><TagDot color={t.color} /><span className="ob-tag__name">{t.name}</span></span>)}</span>;
    default: return row.lastContact ? <span className="ob-dtable__muted">{row.lastContact}</span> : <span className="ob-dtable__muted">—</span>;
  }
}

/** The list page is a table: a customer has attributes, not prose, and attributes compare only when they line up in columns.
 *  Checkboxes appear on hover and stay once anything is selected; a row click opens the profile. `framed` (polish round one, 2026-10-16):
 *  the table sits in its white card on the canvas, as every other table of the app (`ArticleTable`'s `framed`). */
export function CustomerTable({ rows, sort, onSort, onNavigate, onToggle, onToggleAll, empty, framed, loading, selectable = true, tags = false, after }: {
  rows: CustomerRowData[]; sort: string; onSort: (id: string) => void; onNavigate: Navigate;
  /** `range`: shift was held (everything from the last row touched, as the tickets table) */ onToggle: (id: string, checked: boolean, range: boolean) => void; onToggleAll: (checked: boolean) => void; empty?: ReactNode; framed?: boolean;
  /** 2026-10-20: false draws no checkboxes (the phone's table, as the tickets' phone list) */ selectable?: boolean;
  /** polish round two: the rows are on their way; the table keeps its columns and shows grey, still blocks in each cell */
  loading?: boolean;
  /** the customer tags round: show the Tags column (the rows' `tags`) */ tags?: boolean;
  /** the home-and-customers round: under the table, inside its scroll, the way to the next page ("Show more") */ after?: ReactNode;
}) {
  const selected = rows.filter((r) => r.checked).length;
  const columns = columnsOf(tags);
  if (rows.length === 0 && !loading) return <div className="ob-listpage__empty">{empty}</div>;
  const table = (
      <table className={cx("ob-dtable", selected > 0 && "ob-dtable--selecting")}>
        <thead>
          <tr>
            {selectable && <HeadCheck checked={rows.length > 0 && selected === rows.length} some={selected > 0} onChange={onToggleAll} />}
            {columns.map((c) => (
              <th key={c.id} className={cx(c.numeric && "ob-dtable__num", c.hide && `ob-dtable__col--${c.hide}`)} aria-sort={c.sort === sort ? "ascending" : undefined}>
                {c.sort ? <button type="button" className={cx("ob-dtable__sort", c.sort === sort && "ob-dtable__sort--active")} onClick={() => onSort(c.sort!)}>{c.label}{c.sort === sort && <Icon name="arrowDown" size="xs" />}</button> : c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody aria-busy={loading || undefined}>
          {loading && rows.length === 0 && Array.from({ length: 8 }, (_, i) => (
            <tr key={`loading-${i}`} className="ob-dtable__row ob-dtable__row--loading">
              {selectable && <td className="ob-dtable__check" />}
              {columns.map((c) => <td key={c.id} className={cx(c.numeric && "ob-dtable__num", c.grow && "ob-dtable__grow", c.hide && `ob-dtable__col--${c.hide}`)}><Skeleton shape="line" width={c.numeric ? "2em" : "70%"} /></td>)}
            </tr>
          ))}
          {rows.map((r) => (
            <tr key={r.id} className={cx("ob-dtable__row", r.checked && "ob-dtable__row--checked")} onClick={(e) => { if ((e.target as HTMLElement).closest("input, a")) return; onNavigate(r.href, e as unknown as Parameters<Navigate>[1]); }}>
              {selectable && <RowCheck checked={!!r.checked} label={`Select ${r.name}`} onChange={(on, range) => onToggle(r.id, on, range)} />}
              {columns.map((c) => (
                <td key={c.id} className={cx(c.numeric && "ob-dtable__num", c.text && "ob-dtable__col--text", c.grow && "ob-dtable__grow", c.hide && `ob-dtable__col--${c.hide}`)} title={c.text ? (c.id === "company" ? r.company ?? "" : r.email) : undefined}>
                  {c.id === "customer" ? <a className="ob-dtable__link" href={r.href} onClick={(e) => onNavigate(r.href, e)}><Cell row={r} col={c} /></a> : <Cell row={r} col={c} />}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
  );
  return <div className="ob-listpage__scroll">{framed ? <div className="ob-customers--table"><div className="ob-dtable-card">{table}</div></div> : table}{after && <div className="ob-listpage__pager">{after}</div>}</div>;
}

/** Replaces the page head while something is selected. */
/** What to do with the rows that are ticked: a dark bar floating at the top centre of the window, over the top bar. It appears with the
 *  first tick and leaves with the last; the list's own head stays as it is. The same bar, in the same place, for every list. */
/** The selection bar. `whole` (the select-whole-list round, 2026-10-29): the whole list is selected, not only the rows on screen; the bar reads
 *  "All 223 selected" with a "Clear" button instead of the "Select all" link. */
export function BulkBar({ count, total, onSelectAll, onClear, whole, children }: { count: number; total: number; onSelectAll: () => void; onClear: () => void; whole?: boolean; children: ReactNode }) {
  return (
    <div className="ob-bulk" role="toolbar" aria-label="Selection">
      {whole ? <span className="ob-bulk__count">All <strong>{total}</strong> selected</span> : <span className="ob-bulk__count"><strong>{count}</strong> selected</span>}
      {whole ? <button type="button" className="ob-bulk__drop" onClick={onClear}>Clear</button> : count < total && <button type="button" className="ob-bulk__all" onClick={onSelectAll}>Select all {total}</button>}
      <span className="ob-bulk__sep" aria-hidden="true" />
      <div className="ob-bulk__actions">{children}</div>
      <span className="ob-bulk__sep" aria-hidden="true" />
      <button type="button" className="ob-bulk__clear" onClick={onClear} aria-label="Clear selection" data-balloon-pos="down"><Icon name="x" size="sm" /></button>
    </div>
  );
}

/* ---------- profile ---------- */
export interface ProfileChip { icon: IconName; label: string; muted?: boolean; }
export function Chip({ icon, label, muted }: ProfileChip) {
  return <span className={cx("ob-chip", muted && "ob-chip--muted")}>{icon && <Icon name={icon} size="xs" />}{label}</span>;
}

export function ProfileHeader({ initials, name, meta, chips, actions, tabs }: { initials: string; name: string; meta: string; chips: ProfileChip[]; actions?: ReactNode; tabs: ReactNode }) {
  return (
    <header className="ob-profile__head">
      <div className="ob-profile__identity">
        <Avatar initials={initials} size="lg" />
        <div className="ob-profile__who">
          <h1 className="ob-profile__name">{name}</h1>
          <div className="ob-profile__meta">{meta}</div>
          {chips.length > 0 && <div className="ob-profile__chips">{chips.map((c, i) => <Chip key={i} {...c} />)}</div>}
        </div>
        {actions && <div className="ob-profile__actions">{actions}</div>}
      </div>
      {tabs}
    </header>
  );
}

/** One facts row (the summary cards round, 2026-11-01): `note` is a quiet word after the value ("forwards here"); `title` is the hover text
 *  for a value that is not plain text (a link, bold words); `state` shows the value as an On/Off pill; `wrap` lets a long free text run over
 *  up to three lines instead of being cut on one. */
export interface FactRow { k: string; v: ReactNode; note?: string; title?: string; state?: "on" | "off"; wrap?: boolean }

/** the dd's hover text: the row's own title, else the value when it is text, with the note after " · " (the note gives way first on a
 *  narrow card, so the hover holds it too); a value that is not text and has no title carries none */
function factTitle(r: FactRow): string | undefined {
  const base = r.title ?? (typeof r.v === "string" || typeof r.v === "number" ? String(r.v) : undefined);
  if (base === undefined) return undefined;
  return r.note ? `${base} · ${r.note}` : base;
}

function FactValue({ row: r }: { row: FactRow }) {
  return (
    <>
      <span className="ob-facts__value">{r.state ? <Pill tone={r.state}>{r.v}</Pill> : r.v}</span>
      {r.note && <span className="ob-facts__note">{r.note}</span>}
    </>
  );
}

/** Key/value rows: one label column for the whole card, as wide as its longest label (capped), labels on one line, a hairline between
 *  rows; a cut value and a cut label show in full on hover (the long-values rule of 2026-10-17, extended to facts on 2026-11-01). */
export function Facts({ rows, className, rowClassName }: { rows: FactRow[]; /** an extra block class on the list (the customer sources panel round: `ob-customer-source__fields`) */ className?: string; /** and on each row */ rowClassName?: string }) {
  return (
    <dl className={cx("ob-facts", className)}>
      {rows.map((r) => (
        <div className={cx("ob-facts__row", rowClassName)} key={r.k}>
          <dt title={r.k}>{r.k}</dt>
          <dd className={cx("ob-facts__dd", r.wrap && "ob-facts__dd--wrap")} title={factTitle(r)}><FactValue row={r} /></dd>
        </div>
      ))}
    </dl>
  );
}

export interface LicenseCardData {
  id: string; product: string; plan: string; verified: boolean; stateLabel: string;
  facts: FactRow[]; sites: { host: string; since?: string }[]; /** "Show all 12 activated sites" */ more?: string;
  /** explanation strip under the card, used for unverified claims */ note?: string;
}
export function LicenseCard({ license, action, onMore }: { license: LicenseCardData; action?: ReactNode; onMore?: () => void }) {
  return (
    <article className={cx("ob-license", !license.verified && "ob-license--unverified")} aria-label={license.product}>
      <div className="ob-license__head">
        <Icon name={license.verified ? "verified" : "help"} size="sm" className="ob-license__icon" />
        <span className="ob-license__product">{license.product}</span>
        <span className="ob-license__plan">{license.plan}</span>
        <Pill>{license.stateLabel}</Pill>
        {action && <span className="ob-license__action">{action}</span>}
      </div>
      <div className="ob-license__body">
        <Facts rows={license.facts} />
        <div className="ob-license__sites">
          <div className="ob-label">Activated sites</div>
          {license.sites.length === 0 && <div className="ob-license__site ob-dtable__muted">No site activated yet</div>}
          {license.sites.map((s) => <div className="ob-license__site" key={s.host}><Icon name="globe" size="sm" /><span className="ob-license__host">{s.host}</span>{s.since && <span className="ob-license__since">{s.since}</span>}</div>)}
          {license.more && <button type="button" className="ob-link" onClick={onMore}>{license.more}</button>}
        </div>
      </div>
      {license.note && <div className="ob-license__note"><Icon name="info" size="sm" />{license.note}</div>}
    </article>
  );
}

export interface NoteData { id: string; author: string; when: string; body: string; }
export function NoteList({ notes }: { notes: NoteData[] }) {
  return <div className="ob-notes">{notes.map((n) => <article className="ob-note" key={n.id}><div className="ob-note__body">{n.body}</div><div className="ob-note__meta">{n.author} · {n.when}</div></article>)}</div>;
}

/** A titled block in the profile's side column (very wide screens). */
export function SideBlock({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return <section className="ob-side__block"><div className="ob-side__head"><span className="ob-label">{title}</span>{action}</div>{children}</section>;
}
